import { useEffect } from 'react';

/**
 * MusicPlayerGlobal - Gestisce il player YouTube globalmente
 * Questo componente viene montato UNA SOLA VOLTA in App.js
 * e NON viene mai smontato durante la navigazione
 */

// Inizializza lo stato globale IMMEDIATAMENTE (fuori da React)
if (!window.__ytMusicPlayer) {
  window.__ytMusicPlayer = {
    player: null,
    isReady: false,
    isPlaying: false,
    currentTrackIndex: 0,
    volume: 30,
    isMuted: false,
    playlist: [],
    listeners: new Set(),
    
    // Metodi helper
    emit(event, data) {
      this.listeners.forEach(cb => {
        try { cb(event, data); } catch(e) { console.error(e); }
      });
    },
    
    subscribe(callback) {
      this.listeners.add(callback);
      return () => this.listeners.delete(callback);
    },
    
    play() {
      if (this.player && this.isReady) {
        this.player.playVideo();
      }
    },
    
    pause() {
      if (this.player && this.isReady) {
        this.player.pauseVideo();
      }
    },
    
    setVolume(vol) {
      this.volume = vol;
      if (this.player && this.isReady) {
        this.player.setVolume(vol);
      }
    },
    
    mute() {
      this.isMuted = true;
      if (this.player && this.isReady) {
        this.player.mute();
      }
    },
    
    unMute() {
      this.isMuted = false;
      if (this.player && this.isReady) {
        this.player.unMute();
      }
    },
    
    loadVideo(videoId) {
      if (this.player && this.isReady) {
        this.player.loadVideoById(videoId);
      }
    },
    
    seekTo(seconds) {
      if (this.player && this.isReady) {
        this.player.seekTo(seconds, true);
      }
    },
    
    getCurrentTime() {
      if (this.player && this.isReady && typeof this.player.getCurrentTime === 'function') {
        return this.player.getCurrentTime();
      }
      return 0;
    },
    
    getDuration() {
      if (this.player && this.isReady && typeof this.player.getDuration === 'function') {
        return this.player.getDuration();
      }
      return 0;
    }
  };
}

// Funzione per inizializzare il player (chiamata una sola volta)
function initGlobalPlayer() {
  const state = window.__ytMusicPlayer;
  
  // Se già inizializzato, esci
  if (state.player || document.getElementById('yt-music-player-global')) {
    return;
  }
  
  // Helper per i cookie
  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    if (match) {
      try { return JSON.parse(decodeURIComponent(match[2])); } 
      catch { return null; }
    }
    return null;
  };
  
  // Carica dati dai cookie
  const savedPlaylist = getCookie('musicPlayerPlaylist');
  const defaultPlaylist = [
    { id: 'sE3uRRFVsmc', title: 'All I Want for Christmas Is You' },
    { id: 'ajPqWxOIjaI', title: 'Last Christmas' }
  ];
  
  state.playlist = (savedPlaylist && Array.isArray(savedPlaylist) && savedPlaylist.length > 0) 
    ? savedPlaylist 
    : defaultPlaylist;
  state.currentTrackIndex = getCookie('musicPlayerCurrentTrack') ?? 0;
  state.volume = getCookie('musicPlayerVolume') ?? 30;
  
  // Crea container per il player
  const container = document.createElement('div');
  container.id = 'yt-music-player-global';
  container.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;z-index:-1;';
  document.body.appendChild(container);
  
  // Funzione per creare il player
  const createPlayer = () => {
    if (state.player) return;
    
    const videoId = state.playlist[state.currentTrackIndex]?.id || state.playlist[0]?.id;
    
    state.player = new window.YT.Player('yt-music-player-global', {
      height: '1',
      width: '1',
      videoId: videoId,
      playerVars: { autoplay: 0, controls: 0, loop: 0 },
      events: {
        onReady: (event) => {
          console.log('[MusicPlayerGlobal] Player ready');
          state.isReady = true;
          event.target.setVolume(state.volume);
          if (state.isMuted) event.target.mute();
          state.emit('ready', event);
        },
        onStateChange: (e) => {
          if (e.data === window.YT.PlayerState.PLAYING) {
            state.isPlaying = true;
          } else if (e.data === window.YT.PlayerState.PAUSED) {
            state.isPlaying = false;
          } else if (e.data === window.YT.PlayerState.ENDED) {
            state.isPlaying = false;
          }
          state.emit('stateChange', e);
        },
        onError: (e) => {
          console.error('[MusicPlayerGlobal] Player error:', e);
          state.emit('error', e);
        }
      }
    });
  };
  
  // Carica l'API YouTube se non presente
  if (!window.YT || !window.YT.Player) {
    // Controlla se lo script è già stato aggiunto
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      document.body.appendChild(script);
    }
    
    // Attendi che l'API sia pronta
    const originalCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (originalCallback) originalCallback();
      createPlayer();
    };
  } else {
    createPlayer();
  }
}

const MusicPlayerGlobal = () => {
  useEffect(() => {
    // Inizializza il player al mount
    initGlobalPlayer();
    
    // NON fare cleanup - il player deve persistere
    // Il cleanup avviene solo quando l'intera app viene chiusa
  }, []);
  
  // Questo componente non renderizza nulla
  return null;
};

export default MusicPlayerGlobal;
